import React, { Component, PropTypes } from 'react';
import { Link } from 'react-router';
import Tooltip from 'react-tooltip';
import partial from 'lodash/partial';
import moment from 'moment';
import classnames from 'classnames';
import ConfirmationDialog from 'common/components/modal/confirmation_dialog';
import WorkSamplesUpload from 'registration/creative/components/work_samples_upload';
import { REJECTED_SAMPLES_RESUBMISSION_WINDOW } from 'registration/constants';
import { isMobile } from 'common/utils';

export const GROUP_ICON = {
  "Logo & Identity": "logo-design",
  "Web & Mobile Design": "website-design",
  "Graphic Design": "brochure-or-menu-design",
  "Art & Illustration": "illustration-and-graphic-design",
  "Packaging Design": "packaging-design",
  "Product Design": "product-design",
  "Naming & Taglines": "naming-a-business-or-product",
}

export const GROUP_CUSTOM_DESCRIPTION = {
  "Product Design": "The industrial design of actual products. Although product design includes graphic and visual design skills, it requires specialized skills in prototype design, user research, business, strategy and more.",
  "Packaging Design": "The design of product packaging (the containers for the products). If you design package labels and other types of graphics for packaging, submit your work samples in the Graphic Design category (above)."
};

/**
 * Work samples.
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
class WorkSamples extends Component {
  constructor(props) {
    super(props);

    this.state = { modalInfo: null, unselectingGroup: null };

    this.selectGroup = this.selectGroup.bind(this);
    this.unselectGroup = this.unselectGroup.bind(this);
    this.isActive = this.isActive.bind(this);
    this.showModal = this.showModal.bind(this);
    this.renderGroup = this.renderGroup.bind(this);
  }

  componentDidMount() {
    const { fetchSampleGroups, fetchImageSamples, fetchWritingSamples } = this.props;

    fetchSampleGroups();
    fetchImageSamples(true);
    fetchWritingSamples(true);
  }

  showModal(sampleType, group) {
    this.setState({
      modalInfo: {
        type: sampleType,
        group
      }
    });
  }

  selectGroup(group) {
    this.showModal(group.subcategories[0].entry_type, group);
  }

  unselectGroup(group) {
    const { deleteImageSamples, deleteWritingSamples } = this.props;
    const sampleType = group.subcategories[0].entry_type;

    if (sampleType === 'image') {
      deleteImageSamples(group);
    } else if (sampleType == 'name') {
      deleteWritingSamples(group);
    }

    this.setState({ unselectingGroup: null });
  }

  isActive(id) {
    const { samples } = this.props;
    return samples[id] && Object.keys(samples[id]).length > 0;
  }

  renderDescription(group) {
    const customDescription = GROUP_CUSTOM_DESCRIPTION[group.name];
    const hasManySubcats = group.subcategories.length > 5;

    if (customDescription) {
      return <p className="custom-description">{ customDescription }</p>;
    }

    return (
      <div className="sub-categories">
        <h5>Includes the following sub-categories:</h5>
        <div className={classnames("row", {
          "small-up-2": hasManySubcats,
          "small-up-1": !hasManySubcats
        })}>
          { group.subcategories.map((subcat) => (
          <div key={subcat.id} className="subcat columns">
            { subcat.name }
          </div>
          )) }
        </div>
      </div>
    );
  }

  renderSamples(group) {
    const { samples } = this.props;
    const sampleType = group.subcategories[0].entry_type;
    const canBeUpdated = this.canBeUpdated(group);

    return (
      <div className="samples">
        { canBeUpdated &&
        <div className="actions">
          <button className="btn btn-icon-only"
              onClick={partial(this.showModal, sampleType, group)}>
            <i className="fa fa-pencil" />
          </button>
          <button className="btn btn-icon-only"
              onClick={() => this.setState({ unselectingGroup: group })}>
            <i className="fa fa-trash" />
          </button>
        </div> }
        { sampleType === 'image'?
        <div className="images">
          { samples[group.id].sample_1?
          <div>
            { Object.values(samples[group.id]).map((s, i) => <img key={i} src={s} />) }
          </div> :
          <div>
            <strong>Legacy creative</strong><br/>
            <strong>No image samples</strong>
          </div> }
        </div> :
        <div>
          <h5>Required questions answered.</h5>
        </div> }
      </div>
    );
  }

  renderStatusInfo(group) {
    const meta = this.props.samplesMeta[group.id];
    const resubmissionWindowEnd = this.getResubmissionWindowEnd(group);

    if (!meta) {
      return null;
    }

    if (meta.approved) {
      return (
        <div className="status-info text-success">
          You have been approved to participate in this category.
        </div>
      );
    }

    if (meta.approved === false) {
      if (!resubmissionWindowEnd || moment().isAfter(resubmissionWindowEnd)) {
        return (
          <div className="status-info text-warning">
            Your request to participate in this category was denied, but
            you can now submit <strong>new</strong> samples, since more
            than { REJECTED_SAMPLES_RESUBMISSION_WINDOW } months have passed.
          </div>
        );
      }

      return (
        <div className="status-info text-danger">
          Your request to participate in this category was denied.
          You may apply again <strong>after{' '}
          { resubmissionWindowEnd.format('MMM D, YYYY') }</strong>.
        </div>
      );
    }

    if (meta.resubmission_requested) {
      return (
        <div className="status-info text-warning">
          Your samples were not approved but you may resubmit <strong>new</strong> samples
          that show your best work in this category.
        </div>
      );
    }

    return null;
  }

  getResubmissionWindowEnd(group) {
    const meta = this.props.samplesMeta[group.id];
    return meta && meta.processed_at? moment(meta.processed_at).add(
      REJECTED_SAMPLES_RESUBMISSION_WINDOW, 'months'
    ) : null;
  }

  canBeUpdated(group) {
    const meta = this.props.samplesMeta[group.id];
    const resubmissionWindowEnd = this.getResubmissionWindowEnd(group);

    if (!meta) {
      return true;
    }

    if (meta.approved) {
      return false;
    }

    if (meta.approved === false) {
      return !resubmissionWindowEnd || moment().isAfter(resubmissionWindowEnd);
    }

    return true;
  }

  renderGroup(group) {
    const active = this.isActive(group.id);
    const iconName = GROUP_ICON[group.name];
    const icon = require(`assets/images/icons/pap/PAP_cat_icon_${iconName}.svg`);

    return (
      <div key={group.id} className="row group align-middle">
        <div className="columns medium-6 large-5 small-16">
          <div className={classnames("tile", { active })} data-tip={true}
              data-for={`tooltip-${group.id}`} data-event={isMobile()? "click" : undefined}
              onClick={active? undefined : partial(this.selectGroup, group)}>
            <i className="icon" dangerouslySetInnerHTML={{ __html: icon }}/>
            <h4>{ group.name }</h4>
          </div>
          { active &&
          <Tooltip class="tooltip" effect="solid" place="bottom" id={`tooltip-${group.id}`}>
            { this.renderDescription(group) }
          </Tooltip> }
        </div>
        <div className="columns medium-10 large-11 small-16">
          { active? this.renderSamples(group) : this.renderDescription(group) }

          { this.renderStatusInfo(group) }
        </div>
      </div>
    );
  }

  render() {
    const { groups, samples } = this.props;
    const { modalInfo, unselectingGroup } = this.state;

    return (
      <section className="work-samples">
        <h3>Share Your Work</h3>
        <p>
          Our <Link target="_blank" to="/help/joining-and-verification/why-does-crowdspring-verify-my-identity-and-quality-of-work-in-certain-types-of-projects/">vetting process</Link> helps
          ensure we uphold the highest standards of quality on crowdspring.
        </p>
        <p>
          The samples you submit <strong>must be your own original work and may not include
          any stock or clip art</strong>.
        </p>
        <p>
          If you want to work in naming/tagline projects, you'll need to answer qualifying
          questions.
        </p>

        <h4 className="groups-title">In which categories do you plan to work?</h4>
        <p>
          We qualify people in <strong>every</strong> category. Pick the categories in which
          you'd like to work and submit 3 samples of your best work. To qualify to work
          in a category, you can submit samples from <strong>any sub-category listed
          for that category</strong>. You must pick at least one category. You can add additional
          categories later.
        </p>

        <div className="groups">
          { groups.map(this.renderGroup) }
        </div>

        { !!unselectingGroup &&
        <ConfirmationDialog dialogVisible={true} dialogHeader="Un-select category?"
          dialogMessage="Are you sure you want to un-select this category? When you click Yes below, we'll remove any work samples you attached and you won't request to work in this category right now. You can always add this category later."
          toggleDialog={() => this.setState({ unselectingGroup: null })}
          submitAction={partial(this.unselectGroup, unselectingGroup)}
        /> }

        { !!modalInfo &&
        <WorkSamplesUpload onClose={() => this.setState({ modalInfo: null })} {...modalInfo} /> }
      </section>
    );
  }
}

WorkSamples.propTypes = {
  groups: PropTypes.array.isRequired,
  samples: PropTypes.object,
  samplesMeta: PropTypes.object,
  fetchSampleGroups: PropTypes.func.isRequired,
  fetchImageSamples: PropTypes.func.isRequired,
  fetchWritingSamples: PropTypes.func.isRequired,
  deleteImageSamples: PropTypes.func.isRequired,
  deleteWritingSamples: PropTypes.func.isRequired,
};

export default WorkSamples;



// WEBPACK FOOTER //
// ./registration/creative/components/work_samples.jsx